import { useMutation, useQuery } from "@apollo/client";
import OpenInNew from "@mui/icons-material/OpenInNew";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import { alpha } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { encampBrandColors } from "colors";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { CopyableText } from "components/CopyableText";
import { Dialog } from "components/Dialog";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import { MultipleUserPicker, UserOption } from "components/MultipleUserPicker";
import { RightDrawerShell } from "components/RightDrawerShell/RightDrawerShell";
import { MAX_COMMENT_LENGTH } from "encamp-shared/src/violations/comments";
import {
  HarmonizedViolationGovStatus,
  HarmonizedViolationMatchProvenance,
  HarmonizedViolationProgram,
  HarmonizedViolationResolutionStatus,
} from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import {
  EXPIRED_ALT_ID_MATCH_VIOLATIONS,
  SET_EXPIRED_ALT_ID_MATCH_ACCEPTED,
  SET_EXPIRED_ALT_ID_MATCH_REJECTED,
} from "hooks/useExpiredAltIdMatchReviews";
import {
  HARMONIZED_VIOLATION_COMMENTS,
  HARMONIZED_VIOLATION_STATS,
  HARMONIZED_VIOLATIONS,
  SET_HARMONIZED_VIOLATION_ASSIGNEE,
  SET_HARMONIZED_VIOLATION_GOV_STATUS,
  SET_HARMONIZED_VIOLATION_STAFF_ASSIGNEE,
} from "hooks/useHarmonizedViolations";
import pluralize from "pluralize";
import { ReactNode, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { assigneeDisplayName, type ViolationRow } from "./columns";
import { DuplicateOfEditor } from "./DuplicateOfEditor";
import { SeverityProvenance } from "./SeverityProvenance";
import { useCanEditViolationWorkflow } from "./useCanEditViolationWorkflow";
import { firstUserId, useAnnounceAssignment } from "./violationAssigneePicker";
import { ViolationCommentsSection } from "./ViolationCommentsSection";
import {
  formatReportedStatus,
  formatViolationDate,
  GOV_STATUS_LABEL,
  GOV_STATUS_OPTIONS,
  isViolationStale,
  MATCH_PROVENANCE_LABEL,
  PriorOwnerChip,
  ProgramChip,
  REPORTED_STATUS_TOOLTIP,
  pendingMatchReview,
  rejectedMatchReview,
  SectionHeading,
  StaleChip,
} from "./violationDisplay";
import { ViolationDocumentsSection } from "./ViolationDocumentsSection";
import { ViolationFieldNotesSection } from "./ViolationFieldNotesSection";

type HeldMatchGroup = NonNullable<ViolationRow["heldMatch"]>;

/**
 * STAFF-ONLY decision panel for a PENDING held row — a second entry point beside
 * the /staff/violation-match-review queue. It lists the violations so the decision
 * is made against them rather than a bare count, and confirms either outcome:
 * Accept releases them irreversibly downstream (insert-only Airtable sync), while
 * Reject releases nothing and is reversible.
 *
 * Refetches are deliberately not awaited, so a refetch hiccup can't report a
 * committed decision as a failure.
 */
function HeldMatchDecisionPanel({
  heldMatch,
  facilityName,
  onDecided,
}: {
  heldMatch: HeldMatchGroup;
  facilityName: string;
  onDecided: () => void;
}) {
  const alerts = useAlerts();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rejectConfirmOpen, setRejectConfirmOpen] = useState(false);
  const {
    data,
    loading: loadingViolations,
    error: violationsError,
  } = useQuery(EXPIRED_ALT_ID_MATCH_VIOLATIONS, {
    variables: { id: heldMatch.id },
  });
  const violations = data?.expiredAltIdMatchViolations ?? [];
  const [acceptMatch, { loading: accepting }] = useMutation(SET_EXPIRED_ALT_ID_MATCH_ACCEPTED, {
    refetchQueries: [HARMONIZED_VIOLATIONS, HARMONIZED_VIOLATION_STATS],
    update: (cache, { data: result }) => {
      const acceptedAt = result?.setExpiredAltIdMatchAccepted?.acceptedAt;
      if (!acceptedAt) return;
      // Sibling rows share this one normalized entity, so stamping it clears all
      // their chips with no grid refetch.
      cache.modify({
        id: cache.identify({ __typename: "HeldMatchGroup", id: heldMatch.id }),
        fields: { acceptedAt: () => acceptedAt },
      });
    },
  });
  const [rejectMatch, { loading: rejecting }] = useMutation(SET_EXPIRED_ALT_ID_MATCH_REJECTED, {
    refetchQueries: [HARMONIZED_VIOLATIONS, HARMONIZED_VIOLATION_STATS],
    update: (cache, { data: result }) => {
      const rejectedAt = result?.setExpiredAltIdMatchRejected?.rejectedAt;
      if (!rejectedAt) return;
      // Flips sibling chips pending → rejected without a grid refetch.
      cache.modify({
        id: cache.identify({ __typename: "HeldMatchGroup", id: heldMatch.id }),
        fields: { rejectedAt: () => rejectedAt },
      });
    },
  });
  const saving = accepting || rejecting;

  const count = heldMatch.violationCount;
  const handleConfirm = async () => {
    try {
      await acceptMatch({ variables: { id: heldMatch.id, accepted: true } });
      alerts.success("Match accepted");
      setConfirmOpen(false);
      onDecided();
    } catch (e) {
      alerts.error("Could not accept the match. Try again.", e);
    }
  };
  const handleReject = async () => {
    try {
      await rejectMatch({ variables: { id: heldMatch.id, rejected: true } });
      alerts.success("Match rejected");
      setRejectConfirmOpen(false);
      onDecided();
    } catch (e) {
      alerts.error("Could not reject the match. Try again.", e);
    }
  };

  return (
    <Alert
      severity="warning"
      icon={false}
      // Tinted with the staff hue (not warning orange) to match the held chip.
      // severity stays "warning" for the attention-needed semantics.
      sx={{
        mb: 2,
        color: "text.primary",
        bgcolor: alpha(encampBrandColors.pumpkin, 0.08),
        border: `1px solid ${alpha(encampBrandColors.pumpkin, 0.24)}`,
      }}
    >
      <Stack gap={1}>
        <Typography variant="subtitle2" color={encampBrandColors.pumpkin}>
          Held pending confirmation
        </Typography>
        <Typography variant="body2">
          Accepting confirms this match and shows {pluralize("violation", count, true)} to{" "}
          {facilityName}. You can undo it later from Violation Match Review, but any violations
          already sent to the customer&apos;s Airtable can&apos;t be pulled back.
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Matched through alternate ID {heldMatch.altIdValue}
        </Typography>
        {loadingViolations ? <CircularProgress size={16} /> : null}
        {violationsError ? (
          // A failed list blocks the accept rather than silently degrading the
          // decision to a bare count.
          <Alert severity="error" sx={{ py: 0 }}>
            Couldn&apos;t load the violations this group holds. Reopen the row to try again.
          </Alert>
        ) : null}
        {!loadingViolations && !violationsError ? (
          <Stack component="ul" sx={{ m: 0, pl: 2, maxHeight: 160, overflowY: "auto" }}>
            {violations.map((violation) => (
              <Typography key={violation.id} component="li" variant="caption">
                {violation.violationId}
                {violation.violationDescription ? ` — ${violation.violationDescription}` : ""}
              </Typography>
            ))}
          </Stack>
        ) : null}
        {!loadingViolations && !violationsError && violations.length < count ? (
          <Typography variant="caption" color="text.secondary">
            Showing {violations.length} of {pluralize("violation", count, true)}.
          </Typography>
        ) : null}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            variant="text"
            color="staff"
            disabled={saving || Boolean(violationsError)}
            onClick={() => setRejectConfirmOpen(true)}
          >
            Reject match
          </Button>
          <Button
            variant="contained"
            color="staff"
            disabled={saving || Boolean(violationsError)}
            onClick={() => setConfirmOpen(true)}
          >
            Accept match
          </Button>
        </Stack>
      </Stack>
      <ConfirmDialog
        open={rejectConfirmOpen}
        loading={rejecting}
        header="Reject this match?"
        info={`Hide ${pluralize("violation", count, true)} as not belonging to ${facilityName}. They stay hidden from the customer; nothing is released. Reversible from this drawer.`}
        confirmText="Yes, reject match"
        onConfirm={handleReject}
        onClose={() => setRejectConfirmOpen(false)}
      />
      <ConfirmDialog
        open={confirmOpen}
        loading={accepting}
        header="Accept this match?"
        resources={[
          { resourceType: "Alternate ID", resourceName: heldMatch.altIdValue },
          {
            resourceType: "Releases",
            resourceName: `${pluralize("violation", count, true)} for ${facilityName}`,
          },
        ]}
        info="You can undo this later, but any violations already sent to the customer's Airtable stay there."
        confirmText="Yes, accept match"
        onConfirm={handleConfirm}
        onClose={() => setConfirmOpen(false)}
      />
    </Alert>
  );
}

/**
 * STAFF-ONLY panel for a REJECTED held row. Un-rejecting returns the group to the
 * pending backlog, unconfirmed because it only reverses a side-effect-free action.
 */
function HeldMatchRejectedPanel({
  heldMatch,
  onDone,
}: {
  heldMatch: HeldMatchGroup;
  onDone: () => void;
}) {
  const alerts = useAlerts();
  const [unrejectMatch, { loading: saving }] = useMutation(SET_EXPIRED_ALT_ID_MATCH_REJECTED, {
    refetchQueries: [HARMONIZED_VIOLATIONS, HARMONIZED_VIOLATION_STATS],
    update: (cache) => {
      cache.modify({
        id: cache.identify({ __typename: "HeldMatchGroup", id: heldMatch.id }),
        fields: { rejectedAt: () => null },
      });
    },
  });

  const handleUnreject = async () => {
    try {
      await unrejectMatch({ variables: { id: heldMatch.id, rejected: false } });
      alerts.success("Match un-rejected");
      onDone();
    } catch (e) {
      alerts.error("Could not un-reject the match. Try again.", e);
    }
  };

  const count = heldMatch.violationCount;
  return (
    <Alert severity="info" icon={false} sx={{ mb: 2 }}>
      <Stack gap={1}>
        <Typography variant="subtitle2">Rejected</Typography>
        <Typography variant="body2">
          Rejected as not this facility&apos;s violations
          {heldMatch.rejectedAt ? ` on ${formatViolationDate(heldMatch.rejectedAt)}` : ""}.{" "}
          {pluralize("violation", count, true)} stay hidden from the customer. Un-reject to return
          them to the pending queue.
        </Typography>
        <Box>
          <Button
            variant="outlined"
            color="staff"
            disabled={saving}
            onClick={() => void handleUnreject()}
          >
            Un-reject
          </Button>
        </Box>
      </Stack>
    </Alert>
  );
}

type ResolutionStatusValue = HarmonizedViolationGovStatus | "";

/** The select's shown value; "Not set" stands in for the label the field no longer has. */
function resolutionStatusDisplay(selected: unknown): ReactNode {
  const status = GOV_STATUS_OPTIONS.find((option) => option === selected);
  return status ? (
    GOV_STATUS_LABEL[status]
  ) : (
    <Box component="span" sx={{ color: "text.disabled" }}>
      Not set
    </Box>
  );
}

/** Narrows an arbitrary select value to a valid tracked-status enum (or null). */
function toResolutionStatus(value: string): HarmonizedViolationGovStatus | null {
  return Object.values(HarmonizedViolationGovStatus).find((s) => s === value) ?? null;
}

/**
 * Collects the required explanation for a "Closed - No Action Needed" closure. The text
 * lands on the violation's comment thread as part of the same mutation, so a
 * closed violation always carries a reason.
 */
function ClosureReasonDialog({
  saving,
  onConfirm,
  onCancel,
}: {
  saving: boolean;
  onConfirm: (reason: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState("");
  // Trimmed because whitespace is not an explanation: the server rejects it, so
  // the button must not offer a round trip that can only fail.
  const trimmed = reason.trim();
  return (
    <Dialog
      open
      onClose={onCancel}
      fullWidth
      title="Close violation"
      primaryActionButtonProps={{
        disabled: trimmed.length === 0,
        loading: saving,
        onClick: () => onConfirm(trimmed),
        saveText: "Close violation",
        startIcon: null,
      }}
    >
      <TextField
        autoFocus
        fullWidth
        multiline
        minRows={3}
        label="Reason"
        value={reason}
        disabled={saving}
        onChange={(e) => setReason(e.target.value)}
        helperText="Required. Saved to this violation's comments, visible to everyone who can see it."
        slotProps={{ htmlInput: { maxLength: MAX_COMMENT_LENGTH } }}
      />
    </Dialog>
  );
}

/**
 * Editable, user-controlled "Resolution status" — backed by the `govStatus`
 * field, which (unlike EPA's derived `standardizedResolutionStatus`) is
 * preserved across data refreshes, so a user's "Resolved" sticks. EPA's own
 * derived status is shown beside it as read-only context.
 *
 * Mounted with `key={violation.id}` so its local state resets cleanly when a
 * different violation opens (no prop-sync effect). The mutation returns
 * { id, govStatus }; Apollo normalizes by id, so the grid row updates from cache.
 */
function ResolutionStatusEditor({
  violationId,
  initial,
  epaStatus,
  violationEndDate,
  lastSeenAt,
  tenantId,
  readOnly,
}: {
  violationId: string;
  initial: ResolutionStatusValue;
  epaStatus: HarmonizedViolationResolutionStatus;
  violationEndDate: string | null | undefined;
  lastSeenAt: string | null | undefined;
  tenantId: string;
  readOnly: boolean;
}) {
  const alerts = useAlerts();
  const [value, setValue] = useState<ResolutionStatusValue>(initial);
  const [pendingClosure, setPendingClosure] = useState(false);
  const [setGovStatusMutation, { loading: saving, error: saveError }] = useMutation(
    SET_HARMONIZED_VIOLATION_GOV_STATUS
  );

  /** Returns whether the write landed, so a failed closure can keep its modal. */
  const save = async (next: HarmonizedViolationGovStatus | null, reason?: string) => {
    const previous = value;
    setValue(next ?? "");
    try {
      await setGovStatusMutation({
        variables: { tenantId, id: violationId, govStatus: next, closureReason: reason ?? null },
        // A terminal status takes the row out of the default (unresolved) view
        // and off its counts, and Apollo can recompute neither from the returned
        // row. The comment thread is a separate query that only a reason
        // changes: a status carrying none writes no comment, so reverting off a
        // closure keeps the old comment as history.
        refetchQueries: [
          HARMONIZED_VIOLATIONS,
          HARMONIZED_VIOLATION_STATS,
          ...(reason ? [HARMONIZED_VIOLATION_COMMENTS] : []),
        ],
      });
      alerts.success("Resolution status updated");
      return true;
    } catch (e) {
      // Roll the field back to its last-saved value so it doesn't show a change
      // that didn't persist.
      setValue(previous);
      alerts.error("Could not save the resolution status. Try again.", e);
      return false;
    }
  };

  const handleChange = async (raw: string) => {
    const next = toResolutionStatus(raw);
    // The reason is required, so nothing is written until the modal returns one
    // — the select keeps showing the previous status meanwhile, and cancelling
    // leaves it there.
    if (next === HarmonizedViolationGovStatus.ClosedWontDo) {
      setPendingClosure(true);
      return;
    }
    await save(next);
  };

  return (
    <Stack gap={0.5}>
      <SectionHeading>Resolution status</SectionHeading>
      <TextField
        select
        fullWidth
        size="small"
        slotProps={{
          htmlInput: { "aria-label": "Resolution status" },
          select: { displayEmpty: true, renderValue: resolutionStatusDisplay },
        }}
        value={value}
        disabled={saving || readOnly}
        error={Boolean(saveError)}
        helperText={saveError ? "Could not save the resolution status. Try again." : undefined}
        onChange={(e) => void handleChange(e.target.value)}
      >
        <MenuItem value="">—</MenuItem>
        {GOV_STATUS_OPTIONS.map((status) => (
          <MenuItem key={status} value={status}>
            {GOV_STATUS_LABEL[status]}
          </MenuItem>
        ))}
      </TextField>
      {/*
        EPA's own status gets its own row rather than the TextField's helperText:
        a save error would otherwise replace it, and FormHelperText renders a <p>
        that can't legally contain the tooltip's IconButton.
      */}
      <Stack direction="row" alignItems="center" gap={0.25}>
        <Typography variant="caption" color="text.secondary">
          Reported status: {formatReportedStatus(epaStatus, violationEndDate, lastSeenAt)}
        </Typography>
        <InfoIconTooltip
          tooltip={REPORTED_STATUS_TOOLTIP}
          ariaLabel="About reported status"
          iconButtonSx={{ color: (t) => t.palette.grey[400] }}
        />
      </Stack>
      {pendingClosure && (
        <ClosureReasonDialog
          saving={saving}
          onConfirm={async (reason) => {
            // Left open on failure: this field is the only copy of the user's
            // prose, and the toast alone would strand them with an empty form.
            if (await save(HarmonizedViolationGovStatus.ClosedWontDo, reason)) {
              setPendingClosure(false);
            }
          }}
          onCancel={() => setPendingClosure(false)}
        />
      )}
    </Stack>
  );
}

/**
 * Mounted with `key={violation.id}` by the drawer so picker state resets when a
 * different violation opens (mirrors GovStatusEditor's keyed-no-effect pattern).
 */
function AssigneeEditor({
  violation,
  tenantId,
  readOnly,
}: {
  violation: ViolationRow;
  tenantId: string;
  readOnly: boolean;
}) {
  const initialAssignee: UserOption[] | null = violation.assignee
    ? [
        {
          id: violation.assignee.id,
          email: violation.assignee.email,
          name: assigneeDisplayName(violation.assignee),
        },
      ]
    : null;

  const alerts = useAlerts();
  const [assignee, setAssigneeSelection] = useState<UserOption[] | null>(initialAssignee);

  // Refetched because the co-located copies the server also moved aren't returned.
  const [setAssignee, { loading: saving }] = useMutation(SET_HARMONIZED_VIOLATION_ASSIGNEE, {
    refetchQueries: [HARMONIZED_VIOLATIONS, HARMONIZED_VIOLATION_STATS],
  });
  const announceAssignment = useAnnounceAssignment();

  const handleAssigneeChange = async (users: UserOption[] | null) => {
    // The server refuses an assignee who cannot reach the facility, so the
    // optimistic selection has to be undone: the row keeps its old owner, and a
    // picker still showing the refused choice reads as a save that worked.
    const previous = assignee;
    setAssigneeSelection(users);
    try {
      const { data } = await setAssignee({
        variables: { tenantId, id: violation.id, userId: firstUserId(users) },
      });
      const settingAssignee = firstUserId(users) !== null;
      announceAssignment(settingAssignee ? "Assignee updated" : "Assignee cleared", {
        skippedCopyCount: data?.setHarmonizedViolationAssignee.skippedCopyCount ?? 0,
        settingAssignee,
      });
    } catch (e) {
      setAssigneeSelection(previous);
      alerts.error("Could not save the assignee. Try again.", e);
    }
  };

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      <SectionHeading>Assignee</SectionHeading>
      <MultipleUserPicker
        label="Assignee"
        hideLabel
        placeholder="Unassigned"
        multiple={false}
        value={assignee}
        disabled={saving || readOnly}
        onChange={(users) => void handleAssigneeChange(users)}
      />
    </Stack>
  );
}

/**
 * Staff-only counterpart to {@link AssigneeEditor}: a single-select STAFF user
 * picker for the violation's staff assignee. Backed by `staffUsers` (Encamp
 * employees) via the picker's `staffOnly` mode — the backend mutations reject
 * non-staff target users.
 *
 * Mounted with `key={violation.id}` by the drawer so picker state resets when a
 * different violation opens. The mutation returns { id, staffAssignee } so Apollo
 * normalizes the row by id. Rendered only for staff callers.
 */
function StaffAssigneeEditor({
  violation,
  tenantId,
}: {
  violation: ViolationRow;
  tenantId: string;
}) {
  const initialAssignee: UserOption[] | null = violation.staffAssignee
    ? [
        {
          id: violation.staffAssignee.id,
          email: violation.staffAssignee.email,
          name: assigneeDisplayName(violation.staffAssignee),
        },
      ]
    : null;

  const alerts = useAlerts();
  const [assignee, setAssigneeSelection] = useState<UserOption[] | null>(initialAssignee);

  // Refetched because the co-located copies the server also moved aren't returned.
  const [setStaffAssignee, { loading: saving }] = useMutation(
    SET_HARMONIZED_VIOLATION_STAFF_ASSIGNEE,
    { refetchQueries: [HARMONIZED_VIOLATIONS, HARMONIZED_VIOLATION_STATS] }
  );
  const announceAssignment = useAnnounceAssignment();

  const handleAssigneeChange = async (users: UserOption[] | null) => {
    const previous = assignee;
    setAssigneeSelection(users);
    try {
      const { data } = await setStaffAssignee({
        variables: { tenantId, id: violation.id, userId: firstUserId(users) },
      });
      const settingAssignee = firstUserId(users) !== null;
      announceAssignment(settingAssignee ? "Encamp assignee updated" : "Encamp assignee cleared", {
        skippedCopyCount: data?.setHarmonizedViolationStaffAssignee.skippedCopyCount ?? 0,
        settingAssignee,
      });
    } catch (e) {
      setAssigneeSelection(previous);
      alerts.error("Could not save the Encamp assignee. Try again.", e);
    }
  };

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      <SectionHeading>Encamp Assignee</SectionHeading>
      <MultipleUserPicker
        label="Encamp Assignee"
        hideLabel
        placeholder="Unassigned"
        multiple={false}
        staffOnly
        value={assignee}
        disabled={saving}
        onChange={(users) => void handleAssigneeChange(users)}
      />
    </Stack>
  );
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <Stack spacing={0.25}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" component="div">
        {value}
      </Typography>
    </Stack>
  );
}

/** A facility name as a link to its profile, opening in a new tab. */
function FacilityLink({
  facility,
  tenantId,
}: {
  facility: { id: string; name: string };
  tenantId: string;
}) {
  return (
    <Link
      component={RouterLink}
      to={`/o/${tenantId}/facilities/${facility.id}/profile`}
      target="_blank"
      rel="noopener"
      variant="body2"
      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
    >
      {facility.name} <OpenInNew fontSize="inherit" />
    </Link>
  );
}

/**
 * The co-located sites a multi-site violation applies to, shown near the top of
 * the drawer because "which facilities" is top-of-mind, not a footnote.
 *
 * When the representative facility is a genuine tree head (`isColocationPrimary`)
 * it leads and the other sites are indented beneath it as a hierarchy. When the
 * group has no true head, responsibility is shared, so every site is listed
 * together, comma-separated. Renders nothing for a single-site violation.
 */
function ColocationSection({ violation, tenantId }: { violation: ViolationRow; tenantId: string }) {
  if (violation.siteCount <= 1) return null;

  const coLocated = violation.affectedFacilities.filter((f) => f.id !== violation.facility.id);

  return (
    <Stack role="group" aria-label="Affected sites" spacing={0.5} sx={{ mb: 3 }}>
      <SectionHeading>Affected sites ({violation.siteCount})</SectionHeading>
      {violation.isColocationPrimary ? (
        <>
          {/* The primary heads the hierarchy, with the co-located sites indented
              beneath it. Every site here is a link — the same rule the
              shared-responsibility branch follows — so the section is a complete,
              consistent list of affected sites. */}
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
            <FacilityLink facility={violation.facility} tenantId={tenantId} />
            <Typography component="span" variant="caption" color="text.secondary">
              (primary)
            </Typography>
          </Box>
          <Stack
            spacing={0.5}
            sx={{ pl: 2, ml: 0.5, borderLeft: (t) => `2px solid ${t.palette.divider}` }}
          >
            {coLocated.map((f) => (
              <FacilityLink key={f.id} facility={f} tenantId={tenantId} />
            ))}
          </Stack>
        </>
      ) : (
        // No primary: the sites share responsibility, so they read as an equal,
        // comma-separated list rather than a hierarchy.
        <Stack direction="row" sx={{ flexWrap: "wrap", columnGap: 0.5, rowGap: 0.5 }}>
          {violation.affectedFacilities.map((f, i) => (
            <Box key={f.id} sx={{ display: "inline-flex", alignItems: "center" }}>
              <FacilityLink facility={f} tenantId={tenantId} />
              {i < violation.affectedFacilities.length - 1 ? (
                <Typography variant="body2" component="span">
                  ,
                </Typography>
              ) : null}
            </Box>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

/**
 * The read-only EPA summary: a two-column grid of fields (Field returns null
 * for empty values, so gaps close up) and the two external ECHO report links.
 * Facility identity leads.
 */
function ViolationDetailsSection({ violation }: { violation: ViolationRow }) {
  return (
    <Stack spacing={2} sx={{ mt: 1, mb: 3 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          columnGap: 3,
          rowGap: 2,
        }}
      >
        <Field
          label="EPA facility ID"
          value={
            violation.epaFacilityId ? <CopyableText>{violation.epaFacilityId}</CopyableText> : null
          }
        />
        <Field
          label="Registry ID"
          value={violation.registryId ? <CopyableText>{violation.registryId}</CopyableText> : null}
        />
        <Field label="State" value={violation.state} />
        <Field
          label="Responsible Party"
          value={
            violation.matchProvenance === HarmonizedViolationMatchProvenance.SharedFrs ? (
              <PriorOwnerChip />
            ) : (
              MATCH_PROVENANCE_LABEL[violation.matchProvenance]
            )
          }
        />
        <Field label="Agency" value={violation.violationAgency} />
        <Field label="Agency level" value={violation.violationAgencyLevel} />
        <Field label="Violation code" value={violation.violationCode} />
        <Field
          label={
            violation.sourceProgram === HarmonizedViolationProgram.Water ? "DMR limit" : "Pollutant"
          }
          value={violation.pollutantTypeDesc}
        />
        <Field label="Citations" value={violation.citations} />
        <Field label="Citation descriptions" value={violation.citationDescs} />
        <Field label="Resolution method" value={violation.resolutionMethodDesc} />
        <Field label="Schedule event" value={violation.scheduleEventDesc} />
        <Field label="Started" value={formatViolationDate(violation.violationStartDate)} />
        <Field label="Ended" value={formatViolationDate(violation.violationEndDate)} />
        <Field label="Last seen" value={formatViolationDate(violation.lastSeenAt)} />
      </Box>

      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
        {violation.detailLink ? (
          <Button
            component="a"
            href={violation.detailLink}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            variant="text"
            endIcon={<OpenInNew />}
          >
            Violation Report in Echo
          </Button>
        ) : null}
        {violation.epaFacilityLink ? (
          <Button
            component="a"
            href={violation.epaFacilityLink}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            variant="text"
            endIcon={<OpenInNew />}
          >
            Facility Report in Echo
          </Button>
        ) : null}
      </Stack>
    </Stack>
  );
}

export function ViolationDetailDrawer({
  violation,
  tenantId,
  refreshStartedAt,
  onClose,
}: {
  violation: ViolationRow | null;
  tenantId: string;
  /** Start of the latest successful refresh — used to flag a stale violation. */
  refreshStartedAt?: string | null;
  onClose: () => void;
}) {
  const { isStaff } = useCurrentUser();
  const canEditWorkflow = useCanEditViolationWorkflow();
  if (!violation) {
    // No violation selected: render nothing. A closed RightDrawerShell would
    // still leave a drawer paper in the DOM, because the shell sets
    // ModalProps={{ keepMounted: true }}.
    return null;
  }

  const stale = isViolationStale(violation.lastSeenAt, refreshStartedAt);
  const facilityProfilePath = `/o/${tenantId}/facilities/${violation.facility.id}/profile`;
  const pendingHeldMatch = pendingMatchReview(violation.heldMatch, isStaff);
  const rejectedHeldMatch = rejectedMatchReview(violation.heldMatch, isStaff);

  return (
    <RightDrawerShell
      open
      onClose={onClose}
      // Freeze the body-scrolling page behind the drawer. Without this its
      // window scrollbar sits beside the drawer's own, and the long panel
      // (held list, comments, documents) makes it easy to scroll the wrong one.
      disableScrollLock={false}
      // Widened over the default so the two-column details grid breathes.
      width={{ md: 800 }}
      title={
        <Typography variant="h6">
          Violation{" "}
          <Typography component="span" variant="inherit" fontWeight="bold">
            {violation.violationId}
          </Typography>
        </Typography>
      }
    >
      <Box role="region" aria-label="Violation detail">
        {/* At the very top so the decision is the first thing a reviewer sees. */}
        {pendingHeldMatch ? (
          <HeldMatchDecisionPanel
            key={`held-${violation.id}`}
            heldMatch={pendingHeldMatch}
            facilityName={violation.facility.name}
            onDecided={onClose}
          />
        ) : null}
        {rejectedHeldMatch ? (
          <HeldMatchRejectedPanel
            key={`rejected-${violation.id}`}
            heldMatch={rejectedHeldMatch}
            onDone={onClose}
          />
        ) : null}

        {/* Violation title and facility read as subheads with consistent type —
            they are the two things a reader orients on. */}
        <Box sx={{ mb: 2 }}>
          {violation.violationDescription ? (
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {violation.violationDescription}
            </Typography>
          ) : null}
          <Link
            component={RouterLink}
            to={facilityProfilePath}
            target="_blank"
            rel="noopener"
            variant="subtitle1"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontWeight: 600 }}
          >
            {violation.facility.name} <OpenInNew fontSize="inherit" />
          </Link>
        </Box>

        <ViolationFieldNotesSection
          key={`field-notes-${violation.id}`}
          tenantId={tenantId}
          violationId={violation.id}
          fieldNotes={violation.fieldNotes}
          available={violation.fieldNotesAvailable}
          readOnly={!canEditWorkflow}
        />

        {/* Which facilities the violation touches is top-of-mind, so the
            co-located sites sit here near the top rather than in the details. */}
        <ColocationSection violation={violation} tenantId={tenantId} />

        {/* useFlexGap makes spacing a real CSS gap on both axes — without it a
            row Stack only spaces horizontally, so the wide escalation chips
            sat flush against the chip row above them whenever they wrapped. */}
        <Stack
          direction="row"
          spacing={0.5}
          useFlexGap
          alignItems="center"
          sx={{ mb: 2, flexWrap: "wrap", rowGap: 1 }}
        >
          {/* Each chip carries its own hover explanation, so there are no separate
              info icons in this row — the chip is the hover target. */}
          <ProgramChip program={violation.sourceProgram} />
          {stale ? <StaleChip /> : null}
        </Stack>

        {/* Everything about "why this severity" lives in one block — level,
            ranked reasons, and the old-signal caution — replacing the chip
            tooltip, escalation chips, and Permit status since field. */}
        <Box sx={{ mb: 3 }}>
          <SeverityProvenance
            severity={violation.standardizedSeverity}
            determinantDesc={violation.stdSeverityDeterminantDesc}
            determinantCode={violation.stdSeverityDeterminantCode}
            orderStatuteCode={violation.orderStatuteCode}
            permitTerminalDate={violation.permitTerminalDate}
            now={new Date()}
          />
        </Box>

        <SectionHeading>Details</SectionHeading>
        <ViolationDetailsSection violation={violation} />

        <Stack spacing={2} sx={{ mb: 3 }}>
          <ResolutionStatusEditor
            key={`status-${violation.id}`}
            violationId={violation.id}
            initial={violation.govStatus ?? ""}
            epaStatus={violation.standardizedResolutionStatus}
            violationEndDate={violation.violationEndDate}
            lastSeenAt={violation.lastSeenAt}
            tenantId={tenantId}
            readOnly={!canEditWorkflow}
          />
          <DuplicateOfEditor
            key={`duplicate-${violation.id}`}
            violation={violation}
            tenantId={tenantId}
            // Drilling the grid down to this row's duplicates takes the drawer
            // with it, so the re-filtered grid is what the user is left looking at.
            onViewDuplicates={onClose}
            readOnly={!canEditWorkflow}
          />
          <AssigneeEditor
            key={`assignee-${violation.id}`}
            violation={violation}
            tenantId={tenantId}
            readOnly={!canEditWorkflow}
          />
          {isStaff ? (
            <StaffAssigneeEditor
              key={`staff-assignee-${violation.id}`}
              violation={violation}
              tenantId={tenantId}
            />
          ) : null}
        </Stack>

        <ViolationCommentsSection
          key={`comments-${violation.id}`}
          tenantId={tenantId}
          violationId={violation.id}
          facilityId={violation.facility.id}
        />

        <ViolationDocumentsSection
          key={`documents-${violation.id}`}
          tenantId={tenantId}
          violationId={violation.id}
          facilityId={violation.facility.id}
        />
      </Box>
    </RightDrawerShell>
  );
}
